/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  dateFormatter,
  isSame,
  startOfDay,
  timeDiff,
  timeFormatter
} from '@ion/core/src/utils/DateUtil';
import useThemeContext from '../../theme/useThemeContext';

export interface TimeStampType {
  time?: string;
  dateOnly?: boolean;
  timeOnly?: boolean;
  daysAgo?: boolean;
  className?: string;
  notificationCardTimeStamp?: string;
}

/**
 * @function TimeStamp
 *
 * Component for displaying a timestamp. Note the boolean
 * props that determine what is displayed. Priorities: daysAgo
 * will override the calendar date (if under 4 days), and timeOnly will
 * omit any calendar date references and just display the time.
 *
 * @param param.time time passed as a date object
 * @param param.dateOnly will omit any time
 * references and just display the date (unless overriden by daysAgo).
 * @param param.timeOnly will omit any calendar date
 * references and just display the time. Highest priority.
 * @param param.daysAgoForDate will override the calendar date (if under 4 days),
 * @param param.className MUI className that conditionally gets applied to
 * the root span
 */
export const TimeStamp = ({
  time,
  className,
  notificationCardTimeStamp,
  dateOnly = false,
  timeOnly = false,
  daysAgo = false
}: TimeStampType) => {
  const now            = new Date();
  const beginningOfDay = startOfDay(now);
  const isSameDay      = isSame(now, time, 'day');
  const diffDays       = Math.ceil(timeDiff(beginningOfDay, time, 'days'));
  const calendar       = dateFormatter(time, UserPreferences.getSystemDateFormat());
  const hourTime       = timeFormatter(time, UserPreferences.getSystemTimeFormat());

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  function convertDaysAgo() {
    if (isSameDay) return translate('common:time.today');
    if (diffDays === 1) return translate('common:time.yesterday');
    return calendar;
  }

  function convertDaysAgoAt() {
    if (isSameDay || (diffDays === 1)) return hourTime;
    return dateFormatter(time, 'MMM DD, hh:mm A');
  }

  const DateDisplay = ({dateOnly}: {dateOnly: boolean}) => {
    if (isCurrentTheme && !daysAgo) {
      return (
        <span className={notificationCardTimeStamp}>
          <span>{calendar}</span>
          <span>{hourTime}</span>
        </span>
      );
    }
    if (daysAgo && !dateOnly) {
      return <span>{convertDaysAgoAt()}</span>;
    }
    if (daysAgo && dateOnly) {
      return <span>{convertDaysAgo()}</span>;
    }
    if (dateOnly) {
      return <span>{calendar}</span>;
    }
    return <span>{`${calendar} - ${hourTime}`}</span>;
  };

  return (
    <span className={className}>
      {
        timeOnly ? (
          <span>{hourTime}</span>
        ) : (
          <DateDisplay dateOnly={dateOnly} />
        )
      }
    </span>
  );
};

export default TimeStamp;
